import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeftRight, ArrowUpDown, RefreshCw, Wallet as WalletIcon, TrendingUp, Zap, ChevronDown, X, ShieldCheck, Share2 } from 'lucide-react';

// Per-coin brand colors — mirrors Wallet.jsx so icons feel consistent
const COIN_COLORS = {
  BTC: '#F7931A', ETH: '#627EEA', USDT: '#26A17B', BNB: '#F3BA2F',
  SOL: '#9945FF', XRP: '#00AAE4', ADA: '#0033AD', DOGE: '#C2A633',
  MATIC: '#8247E5', DOT: '#E6007A', TRX: '#FF060A', LINK: '#2A5ADA',
  LTC: '#345D9D', AVAX: '#E84142', ATOM: '#2E3148', NEAR: '#00C08B',
  SHIB: '#FFA409', UNI: '#FF007A', XLM: '#7D00FF', ARB: '#28A0F0',
};
const coinColor = (sym) => COIN_COLORS[String(sym || '').toUpperCase()] || '#848E9C';
const USDT_IMG = 'https://assets.coingecko.com/coins/images/325/large/Tether.png';

// Sub-wallet section labels
const WALLET_LABELS = {
  spot: { label: 'Spot Wallet', icon: TrendingUp },
  funding: { label: 'Funding Wallet', icon: WalletIcon },
  coin_m: { label: 'COIN-M Futures', icon: Zap },
};

export default function Convert() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [allWallets, setAllWallets] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [mode, setMode] = useState('instant'); // instant | recurring | limit
  const [walletSection, setWalletSection] = useState('spot');
  const [fromSym, setFromSym] = useState('USDT');
  const [toSym, setToSym] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [pickerFor, setPickerFor] = useState(null); // 'from' | 'to' | null
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSecs, setPreviewSecs] = useState(10);
  const [busy, setBusy] = useState(false);
  // Recurring form
  const [recurInterval, setRecurInterval] = useState('weekly');
  const [recurList, setRecurList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplBusyId, setTplBusyId] = useState(null);
  const [tplLabel, setTplLabel] = useState('');
  const [tplSaving, setTplSaving] = useState(false);
  // Limit form
  const [limitTarget, setLimitTarget] = useState('');
  const [limitList, setLimitList] = useState([]);

  // Load balances + prices
  const loadData = useCallback(async () => {
    try {
      const [aw, mk, rec, lim, tpl] = await Promise.all([
        api.get('/wallet/all'),
        api.get('/markets'),
        api.get('/wallet/recurring_conversions').catch(() => ({ data: { recurring: [] } })),
        api.get('/wallet/limit_conversions').catch(() => ({ data: { limits: [] } })),
        api.get('/wallet/recurring_conversions/templates').catch(() => ({ data: { templates: [] } })),
      ]);
      setAllWallets(aw.data);
      const list = Array.isArray(mk.data) ? mk.data : (mk.data?.markets || []);
      setMarkets(list);
      setRecurList(rec.data?.recurring || []);
      setLimitList(lim.data?.limits || []);
      setTemplates(tpl.data?.templates || []);
    } catch (e) { /* ignore, retry */ }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  // Poll price every 5s so the rate stays fresh (like Binance)
  useEffect(() => {
    const t = setInterval(() => {
      api.get('/markets').then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.markets || []);
        setMarkets(list);
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const priceMap = useMemo(() => {
    const m = { USDT: 1 };
    for (const it of markets) {
      m[String(it.symbol || '').toUpperCase()] = Number(it.current_price) || 0;
    }
    return m;
  }, [markets]);
  const metaMap = useMemo(() => {
    const m = { USDT: { image: USDT_IMG, name: 'Tether', change24h: 0 } };
    for (const it of markets) {
      m[String(it.symbol || '').toUpperCase()] = {
        image: it.image, name: it.name,
        change24h: Number(it.price_change_percentage_24h) || 0,
      };
    }
    return m;
  }, [markets]);

  // Available in the selected sub-wallet for the from-coin
  const balances = useMemo(() => {
    if (!allWallets) return {};
    const section = walletSection === 'spot' ? allWallets.spot
                  : walletSection === 'funding' ? allWallets.funding
                  : allWallets.coin_m;
    const rows = section?.balances || [];
    const m = {};
    for (const b of rows) m[String(b.symbol).toUpperCase()] = Number(b.amount) || 0;
    return m;
  }, [allWallets, walletSection]);

  const fromBal = balances[fromSym] || 0;
  const priceFrom = priceMap[fromSym] || 0;
  const priceTo = priceMap[toSym] || 0;
  const rate = (priceFrom > 0 && priceTo > 0) ? priceFrom / priceTo : 0;
  const change24 = metaMap[toSym]?.change24h || 0;
  const amt = parseFloat(amount) || 0;
  const receiveAmt = rate > 0 ? amt * rate : 0;

  // Coin universe for pickers, filtered by wallet section rules
  const coinList = useMemo(() => {
    const base = ['USDT', ...markets.map(m => String(m.symbol).toUpperCase())].filter((s, i, arr) => arr.indexOf(s) === i);
    if (walletSection === 'coin_m') return base.filter(s => s !== 'USDT'); // COIN-M excludes USDT
    return base;
  }, [markets, walletSection]);
  // If the current sym is invalid for the section, reset
  useEffect(() => {
    if (walletSection === 'coin_m') {
      if (fromSym === 'USDT') setFromSym('BTC');
      if (toSym === 'USDT') setToSym('ETH');
    }
  }, [walletSection]); // eslint-disable-line

  const flipSides = () => { setFromSym(toSym); setToSym(fromSym); };
  const setMaxFromBal = () => setAmount(String(fromBal));

  const canPreview = amt > 0 && amt <= fromBal + 1e-9 && rate > 0 && fromSym !== toSym;
  const validationMsg = (() => {
    if (fromSym === toSym) return 'From and To must be different assets';
    if (!priceFrom || !priceTo) return 'Live price unavailable — try again in a moment';
    if (amt <= 0) return 'Enter an amount';
    if (amt > fromBal + 1e-9) return `Insufficient ${fromSym} in ${WALLET_LABELS[walletSection].label}`;
    return null;
  })();

  // Preview countdown — 10s to confirm at locked rate (visual, no rate-lock actually enforced)
  useEffect(() => {
    if (!previewOpen) return;
    setPreviewSecs(10);
    const iv = setInterval(() => {
      setPreviewSecs(s => {
        if (s <= 1) { clearInterval(iv); setPreviewOpen(false); toast.info('Rate refresh — please preview again'); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [previewOpen]);

  const doConvert = async () => {
    if (!canPreview) return;
    setBusy(true);
    try {
      const r = await api.post('/wallet/convert', {
        from_symbol: fromSym, to_symbol: toSym, amount: amt, wallet: walletSection,
      });
      toast.success(`Converted ${amt} ${fromSym} → ${Number(r.data.to_amount).toFixed(8)} ${toSym}`);
      setPreviewOpen(false);
      setAmount('');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Convert failed');
    } finally { setBusy(false); }
  };

  // Recurring DCA order — fires every {interval} once the next_run_at hits
  const createRecurring = async () => {
    if (amt <= 0 || fromSym === toSym) { toast.error('Enter a valid amount + pick two assets'); return; }
    setBusy(true);
    try {
      await api.post('/wallet/recurring_conversions', {
        from_symbol: fromSym, to_symbol: toSym, amount: amt,
        wallet: walletSection, interval: recurInterval,
      });
      toast.success(`${recurInterval[0].toUpperCase() + recurInterval.slice(1)} DCA created — first run in ${recurInterval === 'daily' ? '24h' : recurInterval === 'weekly' ? '7d' : '30d'}`);
      setAmount('');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setBusy(false); }
  };
  const toggleRecurring = async (recur_id) => {
    try { await api.post(`/wallet/recurring_conversions/${recur_id}/toggle`); loadData(); }
    catch { toast.error('Failed'); }
  };
  const deleteRecurring = async (recur_id) => {
    try { await api.delete(`/wallet/recurring_conversions/${recur_id}`); toast.success('DCA removed'); loadData(); }
    catch { toast.error('Failed'); }
  };
  const applyTemplate = async (template_id) => {
    setTplBusyId(template_id);
    try {
      const r = await api.post('/wallet/recurring_conversions/from_template', { template_id });
      const count = r.data.created?.length || 0;
      toast.success(`Template applied — ${count} DCA order${count === 1 ? '' : 's'} created`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setTplBusyId(null); }
  };
  const saveCurrentAsTemplate = async () => {
    const label = tplLabel.trim();
    if (!label) { toast.error('Give your template a label'); return; }
    if (amt <= 0 || fromSym === toSym) { toast.error('Set amount + two assets first'); return; }
    setTplSaving(true);
    try {
      await api.post('/wallet/recurring_conversions/save_template', {
        label, from_symbol: fromSym, wallet: walletSection, interval: recurInterval,
        default_amount: amt, splits: [{ to_symbol: toSym, weight: 1.0 }],
      });
      toast.success(`Template "${label}" saved`);
      setTplLabel('');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save template'); }
    finally { setTplSaving(false); }
  };
  const deleteTemplate = async (template_id) => {
    try {
      await api.delete(`/wallet/recurring_conversions/templates/${template_id}`);
      toast.success('Template removed');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Only custom templates can be deleted'); }
  };
  const shareTemplate = async (template_id) => {
    try {
      const r = await api.post(`/wallet/recurring_conversions/templates/${template_id}/share`);
      const url = `${window.location.origin}/convert/shared/${r.data.share_id}`;
      try { await navigator.clipboard.writeText(url); toast.success('Share link copied to clipboard'); }
      catch { toast.success(`Share link: ${url}`); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to share'); }
  };

  // Limit order — auto-fills when the market rate crosses the target
  const createLimit = async () => {
    const tgt = parseFloat(limitTarget);
    if (amt <= 0 || !tgt || tgt <= 0 || fromSym === toSym) { toast.error('Set amount and target rate'); return; }
    setBusy(true);
    try {
      const r = await api.post('/wallet/limit_conversions', {
        from_symbol: fromSym, to_symbol: toSym, amount: amt,
        wallet: walletSection, target_rate: tgt,
      });
      toast.success(`Limit set — fills when 1 ${fromSym} ${r.data.direction === 'gte' ? '≥' : '≤'} ${tgt} ${toSym}`);
      setAmount(''); setLimitTarget('');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setBusy(false); }
  };
  const cancelLimit = async (limit_id) => {
    try { await api.delete(`/wallet/limit_conversions/${limit_id}`); toast.success('Canceled'); loadData(); }
    catch { toast.error('Failed'); }
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-24 text-center" data-testid="convert-login-cta">
        <div className="text-white text-xl font-semibold mb-2">Sign in to Convert</div>
        <div className="text-[#848E9C] text-sm mb-6">Instant, zero-fee swaps between assets require an account.</div>
        <Link to="/login" className="inline-block px-5 py-2 bg-[#FCD535] text-[#0B0E11] rounded-sm font-semibold">Log in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="convert-page">
      {/* Page tabs — Binance-style top bar */}
      <div className="flex items-center gap-6 border-b border-[#2B3139] mb-8 overflow-x-auto">
        <span className="text-white text-lg font-semibold pb-3 border-b-2 border-[#FCD535]" data-testid="convert-tab-active">Convert</span>
        <Link to="/trade/spot" className="text-[#848E9C] hover:text-white text-lg font-semibold pb-3">Spot</Link>
        <Link to="/trade/futures" className="text-[#848E9C] hover:text-white text-lg font-semibold pb-3">Futures</Link>
        <Link to="/traders" className="text-[#848E9C] hover:text-white text-lg font-semibold pb-3">Copy</Link>
        <Link to="/wallet" className="text-[#848E9C] hover:text-white text-lg font-semibold pb-3 ml-auto">← Wallet</Link>
      </div>

      <h1 className="font-display text-white text-4xl font-bold mb-1">Convert</h1>
      <p className="text-[#848E9C] text-sm mb-6">Instant swap between assets at the current mid-price. Zero trading fee.</p>

      {/* Mode + wallet selectors */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex bg-[#181A20] border border-[#2B3139] rounded-sm" role="tablist">
          {['instant', 'recurring', 'limit'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-1.5 text-sm uppercase font-mono ${mode === m ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#848E9C] hover:text-white'}`}
              data-testid={`convert-mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
        <div className="text-[10px] uppercase font-mono text-[#848E9C]">Source:</div>
        <div className="inline-flex bg-[#181A20] border border-[#2B3139] rounded-sm" role="tablist">
          {['spot', 'funding', 'coin_m'].map(s => {
            const Icon = WALLET_LABELS[s].icon;
            return (
              <button
                key={s}
                onClick={() => setWalletSection(s)}
                className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${walletSection === s ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#848E9C] hover:text-white'}`}
                data-testid={`convert-wallet-${s}`}
              >
                <Icon className="w-3 h-3" /> {WALLET_LABELS[s].label.replace(' Wallet', '')}
              </button>
            );
          })}
        </div>
        <div className="ml-auto text-[11px] text-[#848E9C] font-mono flex items-center gap-1">
          <ShieldCheck className="w-3 h-3 text-[#0ECB81]" /> Zero trading fee
        </div>
      </div>

      {/* Live rate strip */}
      <div className="border border-[#2B3139] bg-[#0B0E11] px-4 py-2 mb-3 flex items-center gap-4 text-sm" data-testid="convert-live-rate">
        <span className="text-[#848E9C] text-[10px] uppercase font-mono">Live Rate</span>
        <span className="text-white font-mono" data-testid="convert-rate-string">
          1 {fromSym} = {rate > 0 ? rate.toLocaleString(undefined, { maximumFractionDigits: 8 }) : '—'} {toSym}
        </span>
        <span className={`font-mono text-xs ${change24 >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="convert-change-24h">
          {change24 >= 0 ? '+' : ''}{change24.toFixed(2)}%
        </span>
        <button
          onClick={loadData}
          className="ml-auto text-[#848E9C] hover:text-[#FCD535] text-[11px] uppercase font-mono inline-flex items-center gap-1"
          data-testid="convert-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* From card */}
      <CoinCard
        label="From"
        symbol={fromSym}
        meta={metaMap[fromSym]}
        balance={fromBal}
        amount={amount}
        onAmountChange={setAmount}
        editable
        onPickerOpen={() => setPickerFor('from')}
        onMax={setMaxFromBal}
        testIdPrefix="convert-from"
      />

      {/* Flip button */}
      <div className="flex justify-center -my-2 relative z-10">
        <button
          onClick={flipSides}
          className="w-10 h-10 rounded-full bg-[#181A20] border border-[#2B3139] grid place-items-center hover:border-[#FCD535] hover:bg-[#2B3139] transition"
          aria-label="Flip conversion direction"
          data-testid="convert-flip"
        >
          <ArrowUpDown className="w-4 h-4 text-[#FCD535]" />
        </button>
      </div>

      {/* To card (read-only receive amount) */}
      <CoinCard
        label="To"
        symbol={toSym}
        meta={metaMap[toSym]}
        balance={balances[toSym] || 0}
        amount={receiveAmt > 0 ? receiveAmt.toFixed(8) : ''}
        editable={false}
        onPickerOpen={() => setPickerFor('to')}
        testIdPrefix="convert-to"
      />

      {/* Mode-specific controls */}
      {mode === 'instant' && (
        <div className="mt-6" data-testid="convert-mode-panel-instant">
          {validationMsg && (
            <div className="text-[#F0B90B] text-xs font-mono mb-2" data-testid="convert-validation">{validationMsg}</div>
          )}
          <Button
            onClick={() => setPreviewOpen(true)}
            disabled={!canPreview}
            className="w-full h-12 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold text-base disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="convert-preview-btn"
          >
            Preview Conversion
          </Button>
        </div>
      )}

      {mode === 'recurring' && (
        <div className="mt-6 space-y-3" data-testid="convert-mode-panel-recurring">
          {/* One-tap DCA templates for fast onboarding */}
          {templates.length > 0 && (
            <div className="border border-[#2B3139] bg-[#181A20] p-3" data-testid="recurring-templates">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Quick templates · one-tap DCA</div>
                <div className="flex items-center gap-3">
                  <Link to="/marketplace/templates" className="text-[10px] uppercase font-mono text-[#FCD535] hover:underline" data-testid="convert-marketplace-link">
                    Browse Marketplace →
                  </Link>
                  <div className="text-[10px] font-mono text-[#5E6673]">{templates.filter(t => t.is_custom).length} custom · {templates.filter(t => !t.is_custom).length} built-in</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {templates.map(t => (
                  <div key={t.template_id} className="relative group">
                    <button
                      onClick={() => applyTemplate(t.template_id)}
                      disabled={tplBusyId === t.template_id}
                      className={`w-full text-left border ${t.is_custom ? 'border-[#0ECB81]/40' : 'border-[#2B3139]'} hover:border-[#FCD535] bg-[#0B0E11] px-3 py-2 rounded-sm transition disabled:opacity-50`}
                      data-testid={`recurring-template-${t.template_id}`}
                    >
                      <div className="flex items-center gap-1.5">
                        {t.is_custom && (
                          <span className="text-[8px] uppercase font-mono px-1 py-0.5 bg-[#0ECB81]/20 text-[#0ECB81]" data-testid={`template-custom-badge-${t.template_id}`}>Custom</span>
                        )}
                        <div className="text-white text-sm font-semibold truncate">{t.label}</div>
                      </div>
                      <div className="text-[10px] font-mono text-[#848E9C] mt-0.5 flex items-center gap-1 flex-wrap">
                        <span className="uppercase">{t.interval}</span>
                        <span>·</span>
                        <span>{t.default_amount} {t.from_symbol}</span>
                        <span>·</span>
                        {t.splits.length === 1 ? (
                          <span>→ {t.splits[0].to_symbol}</span>
                        ) : (
                          <span>→ {t.splits.map(s => `${Math.round(s.weight * 100)}% ${s.to_symbol}`).join(' + ')}</span>
                        )}
                      </div>
                    </button>
                    {t.is_custom ? (
                      <>
                        <button
                          onClick={() => shareTemplate(t.template_id)}
                          className="absolute top-1 right-8 p-1 text-[#FCD535] hover:bg-[#FCD535]/20 rounded-sm opacity-0 group-hover:opacity-100 transition"
                          data-testid={`template-share-${t.template_id}`}
                          aria-label="Copy share link"
                          title="Copy share link"
                        >
                          <Share2 className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => deleteTemplate(t.template_id)}
                          className="absolute top-1 right-1 p-1 text-[#F6465D] hover:bg-[#F6465D]/20 rounded-sm opacity-0 group-hover:opacity-100 transition"
                          data-testid={`template-delete-${t.template_id}`}
                          aria-label="Delete custom template"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => shareTemplate(t.template_id)}
                        className="absolute top-1 right-1 p-1 text-[#FCD535] hover:bg-[#FCD535]/20 rounded-sm opacity-0 group-hover:opacity-100 transition"
                        data-testid={`template-share-${t.template_id}`}
                        aria-label="Copy share link"
                        title="Copy share link"
                      >
                        <Share2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {/* Save current form as a personal template */}
              <div className="border-t border-[#2B3139] mt-3 pt-3 flex items-center gap-2 flex-wrap" data-testid="template-save-row">
                <span className="text-[10px] uppercase font-mono text-[#848E9C]">Save current setup:</span>
                <input
                  value={tplLabel}
                  onChange={e => setTplLabel(e.target.value.slice(0, 60))}
                  placeholder={`e.g. My ${recurInterval} ${toSym} bag`}
                  className="flex-1 min-w-[180px] bg-[#0B0E11] border border-[#2B3139] focus:border-[#FCD535] outline-none px-2 py-1 rounded-sm text-white text-sm"
                  data-testid="template-save-label"
                />
                <button
                  onClick={saveCurrentAsTemplate}
                  disabled={tplSaving || !tplLabel.trim() || amt <= 0 || fromSym === toSym}
                  className="bg-[#0ECB81] hover:bg-[#0ECB81]/80 text-[#0B0E11] rounded-sm font-semibold px-3 py-1 text-xs uppercase disabled:opacity-40"
                  data-testid="template-save-btn"
                >
                  {tplSaving ? 'Saving…' : 'Save Template'}
                </button>
              </div>
            </div>
          )}
          <div className="border border-[#2B3139] bg-[#181A20] p-3 flex items-center gap-3 flex-wrap">
            <div className="text-[10px] uppercase font-mono text-[#848E9C]">Repeat every</div>
            <div className="inline-flex bg-[#0B0E11] border border-[#2B3139] rounded-sm">
              {['daily', 'weekly', 'monthly'].map(iv => (
                <button
                  key={iv}
                  onClick={() => setRecurInterval(iv)}
                  className={`px-3 py-1.5 text-xs uppercase font-mono ${recurInterval === iv ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#848E9C] hover:text-white'}`}
                  data-testid={`recurring-interval-${iv}`}
                >
                  {iv}
                </button>
              ))}
            </div>
            <div className="ml-auto text-[11px] text-[#848E9C]">DCA · buys automatically at each interval</div>
          </div>
          {validationMsg && <div className="text-[#F0B90B] text-xs font-mono" data-testid="convert-validation">{validationMsg}</div>}
          <Button
            onClick={createRecurring}
            disabled={busy || amt <= 0 || amt > fromBal + 1e-9 || fromSym === toSym}
            className="w-full h-12 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold text-base disabled:opacity-40"
            data-testid="recurring-create-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Create {recurInterval[0].toUpperCase() + recurInterval.slice(1)} DCA
          </Button>
          {recurList.length > 0 && (
            <div className="border border-[#2B3139] bg-[#181A20]" data-testid="recurring-list">
              <div className="px-4 py-2 border-b border-[#2B3139] text-[10px] uppercase font-mono text-[#848E9C] flex items-center justify-between">
                <span>Active recurring orders</span>
                <span>{recurList.filter(r => r.active).length} live</span>
              </div>
              {recurList.map(r => (
                <div key={r.recur_id} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-4 py-3 border-b border-[#2B3139]/60 last:border-b-0" data-testid={`recurring-row-${r.recur_id}`}>
                  <div>
                    <div className="text-white text-sm font-mono">
                      {r.amount} {r.from_symbol} <ArrowLeftRight className="w-3 h-3 inline mx-1 text-[#FCD535]" /> {r.to_symbol}
                      <span className="ml-2 text-[10px] uppercase text-[#848E9C]">· {r.interval} · {r.wallet}</span>
                    </div>
                    <div className="text-[10px] font-mono text-[#5E6673] mt-0.5">
                      {r.runs > 0 ? `${r.runs} runs · ${Number(r.total_to).toFixed(6)} ${r.to_symbol} accumulated` : 'Waiting for first run'}
                      {r.next_run_at && <> · next {new Date(r.next_run_at).toLocaleDateString()}</>}
                    </div>
                    {r.last_error && <div className="text-[10px] text-[#F6465D] mt-0.5">⚠ {r.last_error}</div>}
                  </div>
                  <span className={`text-[10px] uppercase font-mono px-2 py-0.5 ${r.active ? 'bg-[#0ECB81]/20 text-[#0ECB81]' : 'bg-[#5E6673]/20 text-[#5E6673]'}`}>
                    {r.active ? 'Active' : 'Paused'}
                  </span>
                  <button onClick={() => toggleRecurring(r.recur_id)} className="text-[10px] uppercase font-mono text-[#B7BDC6] hover:text-[#FCD535] px-2 py-1 border border-[#2B3139] rounded-sm" data-testid={`recurring-toggle-${r.recur_id}`}>
                    {r.active ? 'Pause' : 'Resume'}
                  </button>
                  <button onClick={() => deleteRecurring(r.recur_id)} className="p-1.5 hover:bg-[#F6465D]/20 text-[#F6465D] rounded-sm" data-testid={`recurring-delete-${r.recur_id}`} aria-label="Delete">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'limit' && (
        <div className="mt-6 space-y-3" data-testid="convert-mode-panel-limit">
          <div className="border border-[#2B3139] bg-[#181A20] p-3">
            <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-2">Target rate · fill when 1 {fromSym} = X {toSym}</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={limitTarget}
                onChange={e => setLimitTarget(e.target.value)}
                placeholder={rate > 0 ? `Current: ${rate.toFixed(8)}` : '0.00'}
                className="flex-1 bg-[#0B0E11] border border-[#2B3139] focus:border-[#FCD535] outline-none px-3 py-2 rounded-sm text-white font-mono text-lg tabular-nums"
                data-testid="limit-target-input"
              />
              <button
                onClick={() => setLimitTarget(String(rate))}
                className="text-[10px] uppercase font-mono text-[#FCD535] hover:text-white border border-[#FCD535]/40 px-2 py-1 rounded-sm"
                data-testid="limit-use-current-rate"
              >
                Use current
              </button>
            </div>
            {limitTarget && rate > 0 && (
              <div className="mt-2 text-[11px] font-mono text-[#848E9C]" data-testid="limit-preview">
                Direction: <span className={parseFloat(limitTarget) >= rate ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>
                  fills when rate {parseFloat(limitTarget) >= rate ? '≥' : '≤'} {parseFloat(limitTarget).toFixed(8)}
                </span>
                {' · '}
                {rate > 0 && (
                  <>Δ {(((parseFloat(limitTarget) - rate) / rate) * 100).toFixed(2)}% from current</>
                )}
              </div>
            )}
          </div>
          {validationMsg && <div className="text-[#F0B90B] text-xs font-mono" data-testid="convert-validation">{validationMsg}</div>}
          <Button
            onClick={createLimit}
            disabled={busy || !canPreview || !limitTarget || parseFloat(limitTarget) <= 0}
            className="w-full h-12 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold text-base disabled:opacity-40"
            data-testid="limit-create-btn"
          >
            Place Limit Convert
          </Button>
          {limitList.length > 0 && (
            <div className="border border-[#2B3139] bg-[#181A20]" data-testid="limit-list">
              <div className="px-4 py-2 border-b border-[#2B3139] text-[10px] uppercase font-mono text-[#848E9C] flex items-center justify-between">
                <span>Limit orders</span>
                <span>{limitList.filter(l => l.status === 'open').length} open</span>
              </div>
              {limitList.map(l => (
                <div key={l.limit_id} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-3 border-b border-[#2B3139]/60 last:border-b-0" data-testid={`limit-row-${l.limit_id}`}>
                  <div>
                    <div className="text-white text-sm font-mono">
                      {l.amount} {l.from_symbol} <ArrowLeftRight className="w-3 h-3 inline mx-1 text-[#FCD535]" /> {l.to_symbol}
                      <span className="ml-2 text-[10px] uppercase text-[#848E9C]">· {l.wallet}</span>
                    </div>
                    <div className="text-[10px] font-mono text-[#5E6673] mt-0.5">
                      target 1 {l.from_symbol} {l.direction === 'gte' ? '≥' : '≤'} {Number(l.target_rate).toFixed(8)} {l.to_symbol}
                      {l.status === 'filled' && <span className="text-[#0ECB81]"> · filled @ {Number(l.filled_rate).toFixed(8)}</span>}
                    </div>
                    {l.last_error && <div className="text-[10px] text-[#F6465D] mt-0.5">⚠ {l.last_error}</div>}
                  </div>
                  <span className={`text-[10px] uppercase font-mono px-2 py-0.5 ${
                    l.status === 'open' ? 'bg-[#FCD535]/20 text-[#FCD535]' :
                    l.status === 'filled' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                    'bg-[#5E6673]/20 text-[#5E6673]'
                  }`}>{l.status}</span>
                  {l.status === 'open' && (
                    <button onClick={() => cancelLimit(l.limit_id)} className="p-1.5 hover:bg-[#F6465D]/20 text-[#F6465D] rounded-sm" data-testid={`limit-cancel-${l.limit_id}`} aria-label="Cancel">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent conversions */}
      <RecentConversions />

      {/* Coin picker */}
      <CoinPicker
        open={!!pickerFor}
        title={pickerFor === 'from' ? 'Select asset to convert from' : 'Select asset to receive'}
        coins={coinList}
        priceMap={priceMap}
        metaMap={metaMap}
        balances={balances}
        showBalance={pickerFor === 'from'}
        currentSym={pickerFor === 'from' ? fromSym : toSym}
        excludeSym={pickerFor === 'from' ? toSym : fromSym}
        onSelect={(sym) => {
          if (pickerFor === 'from') setFromSym(sym); else setToSym(sym);
          setPickerFor(null);
        }}
        onClose={() => setPickerFor(null)}
      />

      {/* Preview confirmation */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md" data-testid="convert-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#FCD535]" /> Confirm Conversion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="border border-[#2B3139] bg-[#0B0E11] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[#848E9C] text-xs">You pay</span>
                <span className="text-white font-mono text-xl" data-testid="preview-pay">{amt.toFixed(8)} {fromSym}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#848E9C] text-xs">You receive</span>
                <span className="text-[#0ECB81] font-mono text-xl" data-testid="preview-receive">≈ {receiveAmt.toFixed(8)} {toSym}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between"><span className="text-[#848E9C]">Rate</span><span className="text-white font-mono">1 {fromSym} = {rate.toFixed(8)} {toSym}</span></div>
              <div className="flex items-center justify-between"><span className="text-[#848E9C]">Inverse</span><span className="text-white font-mono">1 {toSym} = {(1 / rate).toFixed(8)} {fromSym}</span></div>
              <div className="flex items-center justify-between"><span className="text-[#848E9C]">Source</span><span className="text-white">{WALLET_LABELS[walletSection].label}</span></div>
              <div className="flex items-center justify-between"><span className="text-[#848E9C]">Trading Fee</span><span className="text-[#0ECB81] font-semibold">$0.00</span></div>
            </div>
            <div className="text-[10px] text-[#848E9C] text-center pt-1" data-testid="preview-countdown">
              Rate refresh in <span className="text-[#FCD535] font-mono">{previewSecs}s</span>
            </div>
            <Button
              onClick={doConvert}
              disabled={busy || !canPreview}
              className="w-full h-11 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
              data-testid="preview-confirm-btn"
            >
              {busy ? 'Converting…' : 'Confirm Convert'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Reusable coin input card (From / To) --------
function CoinCard({ label, symbol, meta, balance, amount, onAmountChange, editable, onPickerOpen, onMax, testIdPrefix }) {
  const color = coinColor(symbol);
  return (
    <div className="border border-[#2B3139] bg-[#181A20] p-4" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center justify-between text-[10px] uppercase text-[#848E9C] font-mono mb-2">
        <span>{label}</span>
        <span>
          Balance:{' '}
          <span className="text-[#B7BDC6] font-mono" data-testid={`${testIdPrefix}-balance`}>
            {Number(balance || 0).toFixed(8)} {symbol}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onPickerOpen}
          className="flex items-center gap-2 border border-[#2B3139] hover:border-[#FCD535] px-3 py-2 rounded-sm min-w-[130px]"
          data-testid={`${testIdPrefix}-picker`}
        >
          <div
            className="w-7 h-7 rounded-full grid place-items-center overflow-hidden shrink-0"
            style={{ backgroundColor: color + '22', border: `1px solid ${color}44` }}
          >
            {meta?.image ? (
              <img src={meta.image} alt={symbol} className="w-6 h-6 rounded-full" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <span className="text-[10px] font-mono font-bold" style={{ color }}>{symbol?.[0]}</span>
            )}
          </div>
          <div className="flex flex-col items-start min-w-0">
            <span className="text-white font-semibold text-sm leading-none" data-testid={`${testIdPrefix}-symbol`}>{symbol}</span>
            {meta?.name && <span className="text-[9px] text-[#848E9C] truncate max-w-[100px]">{meta.name}</span>}
          </div>
          <ChevronDown className="w-4 h-4 text-[#848E9C] ml-auto" />
        </button>
        <div className="flex-1 flex items-center gap-2 relative">
          <input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={editable ? (e) => onAmountChange(e.target.value) : undefined}
            readOnly={!editable}
            placeholder="0.00"
            className="w-full bg-transparent text-white text-2xl font-mono text-right outline-none placeholder:text-[#3E4753] tabular-nums"
            data-testid={`${testIdPrefix}-amount`}
          />
          {editable && (
            <button
              onClick={onMax}
              className="text-[10px] uppercase font-mono text-[#FCD535] hover:text-white border border-[#FCD535]/40 px-2 py-1 rounded-sm"
              data-testid={`${testIdPrefix}-max`}
            >
              Max
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// -------- Coin picker modal --------
function CoinPicker({ open, title, coins, priceMap, metaMap, balances, showBalance, currentSym, excludeSym, onSelect, onClose }) {
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);
  const filtered = coins
    .filter(s => s !== excludeSym)
    .filter(s => !q.trim() || s.toLowerCase().includes(q.toLowerCase()) || (metaMap[s]?.name || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg p-0 overflow-hidden" data-testid="convert-coin-picker">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="text-base">{title}</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-3">
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search coin (e.g. BTC, Bitcoin)"
            className="w-full bg-[#0B0E11] border border-[#2B3139] focus:border-[#FCD535] outline-none px-3 py-2 rounded-sm text-sm text-white placeholder:text-[#5E6673]"
            data-testid="picker-search"
          />
        </div>
        <div className="max-h-[420px] overflow-y-auto border-t border-[#2B3139]">
          {filtered.length === 0 && (
            <div className="p-6 text-center text-[#848E9C] text-sm">No coins match.</div>
          )}
          {filtered.map(sym => {
            const color = coinColor(sym);
            const price = priceMap[sym] || 0;
            const meta = metaMap[sym] || {};
            const bal = balances[sym] || 0;
            const usd = bal * price;
            return (
              <button
                key={sym}
                onClick={() => onSelect(sym)}
                className={`w-full grid grid-cols-[auto_1fr_auto] gap-3 items-center px-4 py-3 border-b border-[#2B3139]/60 hover:bg-[#2B3139] transition text-left ${sym === currentSym ? 'bg-[#FCD535]/5' : ''}`}
                data-testid={`picker-row-${sym}`}
              >
                <div
                  className="w-8 h-8 rounded-full grid place-items-center overflow-hidden"
                  style={{ backgroundColor: color + '22', border: `1px solid ${color}44` }}
                >
                  {meta.image ? (
                    <img src={meta.image} alt={sym} className="w-6 h-6 rounded-full" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <span className="text-[10px] font-mono font-bold" style={{ color }}>{sym[0]}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm">{sym}</div>
                  <div className="text-[10px] text-[#848E9C] truncate">{meta.name || sym}</div>
                </div>
                <div className="text-right">
                  {showBalance ? (
                    <>
                      <div className="text-white font-mono text-xs">{bal.toFixed(6)}</div>
                      <div className="text-[10px] text-[#848E9C] font-mono">${usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-white font-mono text-xs">${price.toLocaleString(undefined, { maximumFractionDigits: price < 1 ? 6 : 2 })}</div>
                      {typeof meta.change24h === 'number' && (
                        <div className={`text-[10px] font-mono ${meta.change24h >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                          {meta.change24h >= 0 ? '+' : ''}{meta.change24h.toFixed(2)}%
                        </div>
                      )}
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Recent conversions strip --------
function RecentConversions() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get('/wallet/transactions').then(r => {
      const list = (r.data?.transactions || []).filter(t => t.type === 'convert').slice(0, 5);
      setRows(list);
    }).catch(() => {});
  }, []);
  if (rows.length === 0) return null;
  return (
    <div className="mt-10" data-testid="convert-recent">
      <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-2">Recent conversions</div>
      <div className="border border-[#2B3139] bg-[#181A20]">
        {rows.map(t => (
          <div key={t.tx_id} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-2.5 border-b border-[#2B3139]/60 last:border-b-0" data-testid={`convert-recent-row-${t.tx_id}`}>
            <div className="text-white text-sm font-mono">
              {Number(t.amount).toFixed(6)} <span className="text-[#848E9C]">{t.from_symbol}</span>
              <ArrowLeftRight className="w-3 h-3 inline mx-2 text-[#FCD535]" />
              {Number(t.to_amount || 0).toFixed(6)} <span className="text-[#848E9C]">{t.to_symbol}</span>
            </div>
            <div className="text-[10px] uppercase font-mono text-[#848E9C]">{t.wallet}</div>
            <div className="text-[10px] font-mono text-[#5E6673] whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
